import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { jsPDF } from 'jspdf';
import databaseService from '../services/DatabaseService';
import './PremiumReportPage.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ recommendations, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null); // 'valid', 'invalid', 'loading'
  const [discountInfo, setDiscountInfo] = useState(null);
  const [finalPrice, setFinalPrice] = useState(5.00);

  const handleApplyPromo = async () => {
    const trimmedCode = promoCode.trim();
    if (!trimmedCode) return;
    setPromoStatus('loading');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promoCode: trimmedCode })
      });
      const data = await response.json();
      if (data.success) {
        setPromoStatus('valid');
        setDiscountInfo(data.discount);
        // Calculate preview price
        let newPrice = 5.00;
        if (data.percent_off) newPrice = 5.00 * (1 - data.percent_off / 100);
        else if (data.amount_off) newPrice = Math.max(0, 5.00 - (data.amount_off / 100));
        setFinalPrice(newPrice);
      } else {
        setPromoStatus('invalid');
        setFinalPrice(5.00);
      }
    } catch (err) {
      setPromoStatus('invalid');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Create PaymentIntent on server (or check if free)
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: 500, email, promoCode: promoCode ? promoCode.trim() : promoCode })
      });
      
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      // Handle 100% off / Free case
      if (data.isFree) {
        onSuccess(email);
        return;
      }

      // 2. Confirm payment on client
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email }
        }
      });

      if (result.error) throw new Error(result.error.message);

      if (result.paymentIntent.status === 'succeeded') {
        onSuccess(email);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form card">
      <h3>Secure Checkout</h3>
      <div className="price-display">
        {discountInfo && <span className="original-price">$5.00</span>}
        <p className="price-tag">${finalPrice.toFixed(2)} USD</p>
        {discountInfo && <span className="discount-badge">Valid: {discountInfo}</span>}
      </div>
      
      <div className="form-group">
        <label>Email Address</label>
        <input 
          type="email" 
          className="form-control" 
          value={email} 
          onChange={e => setEmail(e.target.value)} 
          required 
          placeholder="For report delivery"
        />
      </div>

      <div className="form-group">
        <label>Coupon Code (Optional)</label>
        <div className="promo-input-wrapper">
          <input 
            type="text" 
            className={`form-control ${promoStatus === 'invalid' ? 'is-invalid' : ''} ${promoStatus === 'valid' ? 'is-valid' : ''}`}
            value={promoCode}
            onChange={e => { setPromoCode(e.target.value); setPromoStatus(null); }}
            placeholder="Enter code"
            disabled={promoStatus === 'valid'}
          />
          <button 
            type="button" 
            className="btn btn-secondary btn-promo" 
            onClick={handleApplyPromo}
            disabled={!promoCode || promoStatus === 'loading' || promoStatus === 'valid'}
          >
            {promoStatus === 'loading' ? '...' : 'Apply'}
          </button>
        </div>
        {promoStatus === 'invalid' && <small className="error-text">Invalid or expired code</small>}
      </div>
      
      {finalPrice > 0 && (
        <div className="form-group">
          <label>Credit or Debit Card</label>
          <div className="stripe-card-container">
            <CardElement options={{ style: { base: { fontSize: '16px', color: '#1e293b' } } }} />
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      
      <button type="submit" disabled={!stripe || loading} className="btn btn-primary btn-large">
        {loading ? 'Processing...' : finalPrice === 0 ? 'Claim Free Report' : 'Pay & Generate Report'}
      </button>
      <p className="secure-note">🔒 SSL Encrypted Checkout via Stripe</p>
    </form>
  );
};

const PremiumReportPage = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    const init = async () => {
      const data = localStorage.getItem('questionnaireData');
      if (!data) { navigate('/cataract-lens-questionnaire'); return; }
      
      await databaseService.initialize();
      setRecommendations(databaseService.getLensRecommendations(JSON.parse(data)));
      setLoading(false);
    };
    init();
  }, [navigate]);

  const generatePDF = (recs, isSample = false) => {
    const doc = new jsPDF();
    const navy = [26, 63, 102];
    const teal = [26, 127, 142];
    const gray = [100, 116, 139];
    const darkText = [30, 41, 59];
    const maxY = 272; // Safe bottom margin above footer

    const data = isSample ? [recs[0]] : recs;

    // --- Helper: check page break ---
    const checkPage = (currentY, needed) => {
      if (currentY + needed > maxY) { doc.addPage(); return 20; }
      return currentY;
    };

    // --- Helper: draw section header banner ---
    const drawSectionHeader = (title) => {
      doc.addPage();
      doc.setFillColor(...navy);
      doc.rect(0, 0, 210, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.text(title, 20, 10);
      return 28;
    };

    // --- Helper: render bulleted list, returns new y ---
    const renderBulletList = (items, startY, maxWidth = 160, color = darkText) => {
      let y = startY;
      items.forEach(item => {
        y = checkPage(y, 10);
        doc.setTextColor(...color);
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(`•  ${item}`, maxWidth);
        doc.text(lines, 25, y);
        y += lines.length * 4.2 + 1.5;
      });
      return y;
    };

    // =========================================================
    // PAGE 1: COVER
    // =========================================================
    doc.setFillColor(...navy);
    doc.rect(0, 0, 210, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text('Clinical Lens Analysis Report', 20, 22);
    doc.setFontSize(10);
    doc.setTextColor(200, 210, 225);
    doc.text('Personalized Intraocular Lens Recommendation & Surgery Guide', 20, 32);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, 39);

    // --- Patient Profile Summary ---
    let y = 55;
    doc.setTextColor(...darkText);
    doc.setFontSize(14);
    doc.text('Your Vision Profile', 20, y);
    doc.setDrawColor(...teal);
    doc.setLineWidth(0.5);
    doc.line(20, y + 3, 190, y + 3);
    y += 12;

    const qData = JSON.parse(localStorage.getItem('questionnaireData') || '{}');
    const profileItems = [];
    // Visual priorities
    const cd = qData.criticalDistances || {};
    const priorities = [];
    if (cd.near) priorities.push('Near');
    if (cd.intermediate) priorities.push('Intermediate');
    if (cd.distance) priorities.push('Distance');
    if (priorities.length) profileItems.push(`Visual Priorities: ${priorities.join(', ')}`);
    // Reading
    const readMap = { avid: 'Avid reader', occasional: 'Occasional reader', minimal: 'Minimal reader', rarely: 'Rarely reads' };
    if (qData.readingHabits) profileItems.push(`Reading Habits: ${readMap[qData.readingHabits] || qData.readingHabits}`);
    // Glasses
    const glassMap = { strongly_dislike: 'Strongly dislikes glasses', fine: 'Completely fine with glasses', mostly_free: 'Mostly free from glasses', prefer: 'Prefers glasses' };
    if (qData.glassesPreference) profileItems.push(`Glasses Preference: ${glassMap[qData.glassesPreference] || qData.glassesPreference}`);
    // Night driving
    const nightMap = { frequent_intolerant: 'Frequent driver, zero tolerance', occasional_tolerant: 'Occasional driver, can tolerate mild halos', occasional: 'Occasional night driver', rare: 'Rarely drives at night' };
    if (qData.nightDriving) profileItems.push(`Night Driving: ${nightMap[qData.nightDriving] || qData.nightDriving}`);
    // Personality
    const persMap = { perfectionist: 'Perfectionist / detail-oriented', easygoing: 'Easy-going / adaptable', adaptable: 'Adaptable & easy-going' };
    if (qData.personality) profileItems.push(`Personality: ${persMap[qData.personality] || qData.personality}`);
    // Eye conditions
    const ec = qData.eyeConditions || {};
    const conditions = [];
    if (ec.amd) conditions.push('Macular Degeneration');
    if (ec.glaucoma) conditions.push('Glaucoma');
    if (ec.dryEye) conditions.push('Dry Eye');
    if (ec.corneal) conditions.push('Corneal Issues');
    if (conditions.length) profileItems.push(`Pre-existing Conditions: ${conditions.join(', ')}`);
    else profileItems.push('Pre-existing Conditions: None reported');
    // Prior surgery
    if (qData.priorSurgery) profileItems.push(`Prior Eye Surgery: ${qData.priorSurgery === 'yes' ? 'Yes (e.g., LASIK/PRK)' : 'No'}`);
    // Astigmatism
    if (qData.astigmatism) profileItems.push(`Astigmatism: ${qData.astigmatism === 'yes' ? 'Yes' : qData.astigmatism === 'no' ? 'No' : 'Unsure'}`);
    // Budget
    const budgetMap = { insurance_only: 'Insurance only', flexible: 'Flexible / open to premium', premium: 'Willing to invest in premium', willing_to_invest: 'Willing to invest out-of-pocket' };
    if (qData.budget) profileItems.push(`Budget: ${budgetMap[qData.budget] || qData.budget}`);

    doc.setFontSize(10);
    profileItems.forEach(item => {
      doc.setTextColor(...darkText);
      const parts = item.split(': ');
      doc.setFont(undefined, 'bold');
      const label = `${parts[0]}:`;
      doc.text(label, 25, y);
      const labelWidth = doc.getTextWidth(label);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...gray);
      doc.text(parts.slice(1).join(': '), 25 + labelWidth + 2, y);
      y += 6;
    });

    // Disclaimer
    y += 5;
    doc.setFillColor(255, 248, 235);
    doc.roundedRect(20, y, 170, 14, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setTextColor(140, 100, 30);
    const disclaimerLines = doc.splitTextToSize('Disclaimer: This report is for educational purposes only and is not a substitute for professional medical advice. Always consult your ophthalmologist for a clinical examination before making any decisions about cataract surgery or lens selection.', 162);
    doc.text(disclaimerLines, 24, y + 4);

    // =========================================================
    // LENS RECOMMENDATION CARDS (with full description, pros, cons, URL)
    // =========================================================
    y = drawSectionHeader('Your Personalized Lens Recommendations');

    data.forEach((lens, i) => {
      // Pre-calculate all content sizes
      doc.setFontSize(9);
      const descLines = doc.splitTextToSize(lens.description, 155);
      const descH = descLines.length * 4;
      const pros = lens.prosAndCons?.pros || [];
      const cons = lens.prosAndCons?.cons || [];
      const hasUrl = lens.manufacturerUrl && lens.manufacturerUrl !== '#';
      // Estimate: header 30 + desc + pros title 8 + pros + cons title 8 + cons + url 8 + padding
      const estProsH = pros.length * 5.5;
      const estConsH = cons.length * 5.5;
      const estTotal = 38 + descH + 8 + estProsH + 8 + estConsH + (hasUrl ? 10 : 0) + 6;

      y = checkPage(y, Math.min(estTotal, 120)); // Force page break if not enough room for header at least

      // Card Background
      doc.setFillColor(248, 250, 252);
      const cardStartY = y;
      // We'll draw the background after we know the final height

      // Lens Name
      doc.setTextColor(...teal);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`${i + 1}. ${lens.name}`, 25, y + 8);
      doc.setFont(undefined, 'normal');

      // Type & Manufacturer
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(10);
      doc.text(`${lens.type}  |  ${lens.manufacturer || ''}`, 25, y + 15);

      // Match Bar
      doc.setFillColor(226, 232, 240);
      doc.rect(25, y + 21, 100, 4, 'F');
      doc.setFillColor(...teal);
      doc.rect(25, y + 21, Math.min(lens.matchPercentage, 100), 4, 'F');
      doc.setTextColor(...darkText);
      doc.setFontSize(10);
      doc.text(`${Math.round(lens.matchPercentage)}% Clinical Match`, 130, y + 25);

      // Price
      if (lens.price) {
        doc.setFontSize(9);
        doc.setTextColor(...gray);
        doc.text(`Est. Cost: ${lens.price.range || 'Varies'}`, 25, y + 32);
      }

      y += 37;

      // Full Description
      doc.setTextColor(...darkText);
      doc.setFontSize(9);
      doc.text(descLines, 25, y);
      y += descH + 3;

      // Pros
      if (pros.length > 0) {
        y = checkPage(y, 12);
        doc.setTextColor(22, 101, 52);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Advantages', 25, y);
        doc.setFont(undefined, 'normal');
        y += 5;
        y = renderBulletList(pros, y, 155, [22, 101, 52]);
      }

      // Cons
      if (cons.length > 0) {
        y = checkPage(y, 12);
        doc.setTextColor(153, 27, 27);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Considerations', 25, y);
        doc.setFont(undefined, 'normal');
        y += 5;
        y = renderBulletList(cons, y, 155, [153, 27, 27]);
      }

      // URL Link
      if (hasUrl) {
        y = checkPage(y, 8);
        doc.setTextColor(37, 99, 235);
        doc.setFontSize(9);
        doc.textWithLink('Learn more from the manufacturer >', 25, y, { url: lens.manufacturerUrl });
        y += 6;
      }

      // Divider between lens cards
      y += 4;
      if (i < data.length - 1) {
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.line(25, y, 185, y);
        y += 8;
      }
    });

    // =========================================================
    // QUESTIONS FOR YOUR SURGEON
    // =========================================================
    y = drawSectionHeader('Questions to Ask Your Surgeon');

    const questions = [
      { q: "Based on my corneal topography and OCT results, am I a good candidate for the lenses recommended in this report?", why: "Your surgeon's measurements may reveal irregularities that influence lens choice." },
      { q: "How does my specific astigmatism profile (type, axis, and magnitude) affect the expected outcome with this lens?", why: "Irregular astigmatism may reduce the effectiveness of certain premium IOLs." },
      { q: "What is your personal surgical volume and success rate with this specific lens platform?", why: "Surgeon experience with a specific IOL directly impacts visual outcomes." },
      { q: "If we choose an EDOF lens, what is the expected functional 'sweet spot' range for intermediate and near vision?", why: "EDOF lenses vary in their intermediate-to-near coverage profile." },
      { q: "What is the likelihood that I will need a YAG capsulotomy (laser treatment for posterior capsule opacification) within 5 years?", why: "Some IOL materials and designs have higher PCO rates than others." },
      { q: "What is the typical neuroadaptation period for this lens, and what percentage of your patients are fully adapted by 3 months?", why: "Your brain needs time to adjust to new optics, especially with multifocal/EDOF designs." },
      { q: "Would a micro-monovision strategy (targeting one eye for slight near bias) be appropriate for my lifestyle and visual goals?", why: "Blending targets between eyes can expand functional range with minimal compromise." },
      { q: "If I am not satisfied with my visual outcome, what enhancement options are available (e.g., LASIK touch-up, lens exchange)?", why: "Understanding your safety net before surgery reduces post-operative anxiety." },
      { q: "Do you use intraoperative aberrometry (e.g., ORA) or advanced planning tools for lens power calculations?", why: "Technology-assisted calculations can improve refractive accuracy, especially for post-LASIK eyes." },
      { q: "Are there any contraindications specific to my eye health that would make one lens category clearly superior to another?", why: "Conditions like mild epiretinal membrane or early glaucoma can steer the choice." }
    ];

    questions.forEach(({ q, why }) => {
      y = checkPage(y, 18);
      doc.setTextColor(...darkText);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      const qLines = doc.splitTextToSize(`•  ${q}`, 165);
      doc.text(qLines, 22, y);
      y += qLines.length * 4.5;
      doc.setFont(undefined, 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...gray);
      const wLines = doc.splitTextToSize(`Why this matters: ${why}`, 160);
      doc.text(wLines, 27, y);
      y += wLines.length * 3.8 + 5;
    });

    // =========================================================
    // WHAT TO EXPECT: PRICING
    // =========================================================
    y = drawSectionHeader('What to Expect: Cataract Surgery Costs');

    doc.setTextColor(...darkText);
    doc.setFontSize(10);
    const pricingIntro = doc.splitTextToSize(
      'Understanding the financial aspects of cataract surgery helps you plan and avoid surprises. Medicare and most insurance plans cover the basic cataract procedure and a standard monofocal lens. Choosing a premium IOL involves additional out-of-pocket costs for the advanced technology.', 170);
    doc.text(pricingIntro, 20, y);
    y += pricingIntro.length * 4.5 + 5;

    const pricingData = [
      ['Lens Category', 'Estimated Additional Cost (per eye)', 'What\'s Included'],
      ['Standard Monofocal', '$0 – covered by insurance', 'Basic distance-only lens'],
      ['Toric (Astigmatism)', '$1,500 – $3,500', 'Astigmatism correction, distance focus'],
      ['EDOF', '$2,000 – $4,500', 'Extended range: distance + intermediate'],
      ['Multifocal / Trifocal', '$2,000 – $4,000', 'Distance, intermediate, and near vision'],
      ['Light Adjustable Lens', '$2,500 – $4,000+', 'Post-surgery fine-tuning with UV light'],
    ];

    // Simple table
    const colWidths = [42, 55, 73];
    const tableX = 20;
    let tableY = y;
    pricingData.forEach((row, ri) => {
      tableY = checkPage(tableY, 10);
      let cellX = tableX;
      const isHeader = ri === 0;
      if (isHeader) {
        doc.setFillColor(...navy);
        doc.rect(tableX, tableY - 4, 170, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
      } else {
        doc.setFillColor(ri % 2 === 0 ? 245 : 255, ri % 2 === 0 ? 247 : 255, ri % 2 === 0 ? 250 : 255);
        doc.rect(tableX, tableY - 4, 170, 8, 'F');
        doc.setTextColor(...darkText);
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
      }
      row.forEach((cell, ci) => {
        doc.text(cell, cellX + 2, tableY);
        cellX += colWidths[ci];
      });
      tableY += 8;
    });
    y = tableY + 5;

    y = checkPage(y, 20);
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    const pricingNotes = doc.splitTextToSize(
      'Note: These are national average estimates and vary by geographic region, surgeon, and facility. The out-of-pocket cost is the difference between the premium lens and the standard lens covered by insurance. Laser-assisted cataract surgery (femtosecond laser) may add $500–$1,500 per eye. Always confirm costs with your surgical center before the procedure.', 170);
    doc.text(pricingNotes, 20, y);
    y += pricingNotes.length * 4 + 3;

    // =========================================================
    // WHAT TO EXPECT: SURGERY PREPARATION & DAY-OF
    // =========================================================
    y = drawSectionHeader('What to Expect: Before & During Surgery');

    doc.setTextColor(...darkText);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Preparing for Surgery', 20, y);
    doc.setFont(undefined, 'normal');
    y += 7;

    const prepItems = [
      'Your surgeon will perform a comprehensive eye exam and take precise measurements (biometry) to select the correct lens power for your eye.',
      'If you wear contact lenses, you may need to stop wearing them 1–3 weeks before your pre-operative measurements to ensure accuracy.',
      'Discuss all medications, supplements, and blood thinners with your surgeon. You may need to temporarily stop certain medications.',
      'You will receive prescriptions for antibiotic and anti-inflammatory eye drops to start 1–3 days before surgery.',
      'Do not eat solid food after midnight the night before surgery. Clear liquids (water, clear tea) are usually permitted until a few hours before.',
      'On surgery day, do not wear eye makeup, perfume, aftershave, or nail polish. Wear loose, comfortable clothing.',
      'Arrange a responsible adult to drive you home and stay with you for the first 24 hours after surgery.'
    ];
    y = renderBulletList(prepItems, y, 165, darkText);

    y = checkPage(y, 15);
    doc.setTextColor(...darkText);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('On the Day of Surgery', 20, y);
    doc.setFont(undefined, 'normal');
    y += 7;

    const dayOfItems = [
      'You will check in at the surgical center, and a nurse will take your vital signs, review your health history, and start an IV for mild sedation.',
      'Eye drops will be administered to dilate your pupil and numb the surface of your eye. You will be awake but relaxed.',
      'The surgeon makes a tiny incision (2–3 mm), uses ultrasound energy (phacoemulsification) to gently break up and remove the cloudy lens, and then inserts the new artificial intraocular lens (IOL).',
      'The entire procedure typically takes only 10–15 minutes per eye. You may see bright lights and movement but will not feel pain.',
      'After surgery, you will rest in a recovery area for 15–30 minutes. A protective shield will be placed over your eye.',
      'You will receive detailed post-operative instructions for eye drops, activity restrictions, and follow-up appointments before discharge.'
    ];
    y = renderBulletList(dayOfItems, y, 165, darkText);

    // =========================================================
    // WHAT TO EXPECT: RECOVERY
    // =========================================================
    y = drawSectionHeader('What to Expect: Recovery Timeline');

    const recoveryBlocks = [
      {
        title: 'First 24–48 Hours',
        items: [
          'Mild discomfort, scratchiness, redness, and light sensitivity are normal.',
          'Your vision may be blurry or hazy as the eye begins to heal.',
          'Use prescribed antibiotic and anti-inflammatory eye drops as directed.',
          'Wear the protective eye shield, especially while sleeping.',
          'Do not rub, touch, or press on your eye.'
        ]
      },
      {
        title: 'Week 1: Gentle Healing',
        items: [
          'Vision will improve noticeably — colors may appear brighter and more vivid.',
          'Avoid strenuous exercise, heavy lifting (over 10 lbs), and bending below your waist.',
          'Light activities like reading, watching TV, and walking are fine.',
          'Wear sunglasses outdoors to protect from UV and bright light.',
          'Do not swim, use hot tubs, or expose your eye to soapy water.',
          'A follow-up appointment with your surgeon is typically scheduled during this week.'
        ]
      },
      {
        title: 'Weeks 2–3: Significant Improvement',
        items: [
          'Most patients report substantially clearer vision with reduced glare.',
          'Light exercise and most routine daily activities can be gradually resumed.',
          'Driving may be possible once your surgeon confirms your vision meets legal requirements.',
          'Continue using eye drops as directed; do not discontinue early.',
          'Avoid eye makeup for at least 2–4 weeks.'
        ]
      },
      {
        title: 'Weeks 4–8: Full Recovery',
        items: [
          'Your eye is mostly healed and vision stabilizes at its clearest level.',
          'A final glasses prescription (if needed) is typically given at this stage.',
          'You can resume swimming, contact sports, and all normal activities.',
          'Some patients with multifocal or EDOF lenses may continue to experience neuroadaptation improvements for up to 3–6 months.',
          'Report any new symptoms like sudden floaters, flashes, or vision changes to your surgeon immediately.'
        ]
      }
    ];

    recoveryBlocks.forEach(block => {
      y = checkPage(y, 20);
      doc.setTextColor(...teal);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(block.title, 22, y);
      doc.setFont(undefined, 'normal');
      y += 6;
      y = renderBulletList(block.items, y, 160, darkText);
      y += 3;
    });

    // =========================================================
    // GLOSSARY OF CLINICAL TERMS
    // =========================================================
    y = drawSectionHeader('Glossary of Clinical Terms');

    const glossary = [
      { term: 'Monofocal IOL', def: 'An intraocular lens that provides clear vision at a single focal point, typically distance. Reading glasses are usually required for near tasks.' },
      { term: 'Multifocal / Trifocal IOL', def: 'A lens with multiple optical zones that provides vision at two or three distances (far, intermediate, near), reducing or eliminating the need for glasses.' },
      { term: 'EDOF (Extended Depth of Focus)', def: 'A lens technology that elongates the focal point to create a continuous range of vision from distance through intermediate, with fewer visual disturbances than traditional multifocals.' },
      { term: 'Toric IOL', def: 'A lens specifically designed to correct astigmatism (an irregularly shaped cornea) in addition to removing the cataract.' },
      { term: 'Accommodating IOL', def: 'A lens designed to move or flex inside the eye, mimicking the natural focusing mechanism to provide a range of vision.' },
      { term: 'Dysphotopsia', def: 'Unwanted visual phenomena such as halos (rings around lights), glare (starburst patterns), or shadows in peripheral vision that some patients experience after IOL implantation.' },
      { term: 'Neuroadaptation', def: 'The brain\'s gradual process of learning to interpret and optimize the visual signals from a new IOL. This process can take weeks to months.' },
      { term: 'Posterior Capsule Opacification (PCO)', def: 'A common condition where the thin membrane behind the IOL becomes cloudy over time, causing blurred vision. Treated easily with a quick YAG laser procedure.' },
      { term: 'YAG Capsulotomy', def: 'A painless, 5-minute outpatient laser procedure to clear a cloudy posterior capsule (PCO) and restore clear vision.' },
      { term: 'Monovision', def: 'A strategy where one eye is targeted for distance and the other for near vision, reducing overall dependence on glasses.' },
      { term: 'Contrast Sensitivity', def: 'The ability to distinguish objects from their background, especially in low-light conditions. Some premium IOLs may reduce contrast sensitivity slightly.' },
      { term: 'Phacoemulsification', def: 'The standard modern cataract surgery technique that uses ultrasound energy to break up and remove the cloudy natural lens through a small incision.' },
    ];

    glossary.forEach(({ term, def }) => {
      y = checkPage(y, 14);
      doc.setTextColor(...teal);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(term, 22, y);
      doc.setFont(undefined, 'normal');
      y += 4.5;
      doc.setTextColor(...darkText);
      doc.setFontSize(9);
      const defLines = doc.splitTextToSize(def, 163);
      doc.text(defLines, 25, y);
      y += defLines.length * 4 + 4;
    });

    // =========================================================
    // FOOTER ON ALL PAGES
    // =========================================================
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(...gray);
      doc.text(`Page ${i} of ${pageCount}  |  CataractLensAdvisor.com  |  For educational purposes only — consult your ophthalmologist`, 105, 287, { align: 'center' });
    }

    if (isSample) {
      window.open(doc.output('bloburl'), '_blank');
    } else {
      doc.save('cataract-lens-report.pdf');
    }
    return doc;
  };

  const handleSuccess = async (email) => {
    setPaymentComplete(true);
    const doc = generatePDF(recommendations);
    
    // Send email via server
    const pdfBase64 = doc.output('datauristring').split(',')[1];
    await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: email,
        subject: 'CataractLensAdvisor: Your Clinical Report',
        text: 'Attached is your personalized lens recommendation report.',
        attachments: [{ content: pdfBase64, filename: 'lens-report.pdf', type: 'application/pdf' }]
      })
    });
  };

  if (loading) return null;

  return (
    <div className="premium-report-page">
      <div className="container">
        <h1 className="section-title">Finalize Your Report</h1>
        <div className="premium-grid">
          <div className="report-preview">
            <div className="preview-card card">
               <div className="preview-header">
                 <span>Report Preview</span>
                 <button className="btn btn-secondary btn-sm" onClick={() => generatePDF(recommendations, true)}>View Sample PDF</button>
               </div>
               <div className="preview-body">
                 <h3>Clinical Analysis Summary</h3>
                 <p>Generated for your 9-question profile.</p>
                 <div className="skeleton-line"></div>
                 <div className="skeleton-line short"></div>
                 <div className="skeleton-block"></div>
               </div>
            </div>
          </div>
          
          <div className="checkout-section">
            <Elements stripe={stripePromise}>
              <CheckoutForm recommendations={recommendations} onSuccess={handleSuccess} />
            </Elements>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumReportPage;
